<template>
  <div v-if="showNotifications" class="wrapper">
    <DialogViewTask
      v-if="isOpenViewTask"
      v-model="isOpenViewTask"
      :task-id="selectedTaskId"
      @task-updated="onTaskResolved"
    />
    <v-carousel
      v-model="activeSlide"
      hide-delimiters
      :show-arrows="false"
      height="auto"
    >
      <v-carousel-item v-for="alert in alerts" :key="alert.id">
        <v-alert
          :type="alert.type"
          :icon="alert.icon"
          border="left"
          text
          outlined
        >
          <div v-if="alert.title" class="text-h6 mb-4">{{ alert.title }}</div>
          <div>{{ alert.message }}</div>
          <div v-if="alert.actions" class="d-flex mt-4">
            <v-spacer />
            <v-btn
              v-for="(value, name) in alert.actions"
              :key="name"
              text
              :color="alert.type"
              @click="handleAction(name, alert)"
            >
              {{ value }}
            </v-btn>
          </div>
        </v-alert>
      </v-carousel-item>
    </v-carousel>
    <div class="controls grey--text">
      {{ activeSlide + 1 }}/{{ totalSlides }}
      <v-btn
        icon
        small
        class="nav-button prev ml-2"
        color="grey"
        @click="decrementSlide"
      >
        <v-icon small>fas fa-chevron-left</v-icon>
      </v-btn>
      <v-btn
        icon
        small
        class="nav-button next"
        color="grey"
        @click="incrementSlide"
      >
        <v-icon small>fas fa-chevron-right</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRoute } from "vue-router/composables";
import useNavigation from "@layout/useNavigation";
import useNotifications from "./useNotifications";
import DialogViewTask from "../manage_tasks/DialogViewTask.vue";
import {
  restoreDocumentTitle,
  flashDocumentTitle,
  severityColor,
  isTaskNotification,
} from "./helper";
import { titleCase } from "@tod-ui/helpers/strings";

export default {
  name: "GlobalNotifications",
  components: {
    DialogViewTask,
  },
  setup() {
    const $route = useRoute();
    const { isCurrentRoute } = useNavigation();
    const {
      notifications,
      unreadErrorNotifications,
      dismissNotification,
      markNotificationRead,
      onTaskResolved,
    } = useNotifications();
    const alerts = ref([]);
    const activeSlide = ref(0);
    const totalSlides = computed(() => alerts.value.length);
    function incrementSlide() {
      return (activeSlide.value = (activeSlide.value + 1) % totalSlides.value);
    }
    function decrementSlide() {
      return (activeSlide.value =
        (totalSlides.value - 1 + activeSlide.value) % totalSlides.value);
    }

    const isNotificationPage = computed(() => $route?.name === "Notifications");
    const showNotifications = computed(
      () =>
        unreadErrorNotifications.value?.length > 0 && !isNotificationPage.value
    );

    watch(
      unreadErrorNotifications,
      (notifications) => {
        if (notifications.length === 0) {
          restoreDocumentTitle();
        } else {
          flashDocumentTitle();
        }

        //todo include current read notification
        const updatedAlerts = [];
        for (const notification of notifications) {
          const {
            title,
            body: message,
            id,
            hasResolution,
            resolutionRoute,
            isDismissible,
            triggeringEntities,
          } = notification;

          const actions = {};
          if (isTaskNotification(notification)) {
            actions.viewTask = "View Task";
          } else if (hasResolution) {
            actions.resolve = "Resolve";
          }
          if (isDismissible) {
            actions.dismiss = "Dismiss";
          }

          updatedAlerts.push({
            type: "error",
            title: title || titleCase(notification.name),
            message,
            item: { id, resolutionRoute, triggeringEntities },
            actions: Object.keys(actions).length > 0 ? actions : undefined,
          });
        }
        alerts.value = updatedAlerts;
        maybeAddCurrentNotification();
      },
      { immediate: true }
    );

    function maybeAddCurrentNotification() {
      const currentIndex = alerts.value.findIndex(
        (alert) =>
          alert?.item?.resolutionRoute &&
          isCurrentRoute(alert.item.resolutionRoute)
      );
      if (currentIndex !== -1) {
        activeSlide.value = currentIndex;
      } else {
        const currentNotification = notifications.value.find(
          (notification) =>
            notification.hasResolution &&
            isCurrentRoute(notification.resolutionRoute)
        );
        if (currentNotification) {
          const actions = {};
          if (isTaskNotification(currentNotification)) {
            actions.viewTask = "View Task";
          }
          if (currentNotification.isDismissible) {
            actions.dismiss = "Dismiss";
          }

          alerts.value.push({
            type: severityColor(currentNotification.severity),
            title:
              currentNotification.title || titleCase(currentNotification.name),
            message: currentNotification.body,
            item: {
              id: currentNotification.id,
              resolutionRoute: currentNotification.resolutionRoute,
              triggeringEntities: currentNotification.triggeringEntities,
            },
            actions: Object.keys(actions).length > 0 ? actions : undefined,
          });
          activeSlide.value = alerts.value.length - 1;
        }
      }
    }

    onMounted(() => {
      if (unreadErrorNotifications.value.length > 0) flashDocumentTitle();
    });
    onUnmounted(restoreDocumentTitle);

    return {
      activeSlide,
      totalSlides,
      incrementSlide,
      decrementSlide,
      alerts,
      showNotifications,
      isCurrentRoute,
      dismissNotification,
      markNotificationRead,
      onTaskResolved,
    };
  },
  data: () => ({
    isOpenViewTask: false,
    selectedTaskId: null,
  }),
  methods: {
    async dismiss(alert) {
      dismissNotification(this.$apollo, alert.item);
    },
    handleAction(name, alert) {
      if (name === "viewTask") {
        this.openTaskDialog(alert);
      } else if (name === "resolve") {
        this.gotoNotificationResolution(alert);
      } else {
        this.dismiss(alert);
      }
    },
    openTaskDialog(alert) {
      const firstEntity = JSON.parse(alert.item.triggeringEntities[0]);
      this.isOpenViewTask = true;
      this.selectedTaskId = firstEntity.id;
    },
    gotoNotificationResolution(alert) {
      const redirect = alert.item?.resolutionRoute;
      if (!redirect || !alert.item?.id || this.isCurrentRoute(redirect)) return;
      if (redirect.name === "Notifications") {
        this.$router.push(redirect);
      } else {
        this.$router.push(redirect, () =>
          this.markNotificationRead(this.$apollo, alert.item)
        );
      }
    },
  },
};
</script>

<style scoped>
.wrapper {
  position: relative;
}

.controls {
  position: absolute;
  top: 0.5em;
  right: 1em;
  display: flex;
  align-items: center;
}
</style>
